"use client";
import type { FC } from "react";

import type { BuilderBlock, BuilderContextInterface, RegisteredComponents } from "@builder.io/sdk-react";
import { Blocks, isEditing } from "@builder.io/sdk-react";

import { useConfig } from "@jti/app-context/useConfig";
import { ContentGate as ContentGateUI } from "@jti/ui/components/ContentGate";
import { ContentGateIP } from "@jti/ui/components/ContentGateIP";
import { ContentGatePopup as ContentGatePopupUI } from "@jti/ui/components/ContentGatePopup";
import { DeviceContentGate, type ProductFamilyOptionsType } from "@jti/ui/components/DeviceContentGate";
import { HardContentGate as HardContentGateUI } from "@jti/ui/components/HardContentGate";

import { env } from "@/env";
import { useLanguages } from "@/hooks/useLanguages";
import { useDisplayModelComponent } from "@/libs/builder/hooks/useDisplayModelComponent";
import { isModelInHardGateConfig } from "@/libs/hardGate/parseHardGateConfig";

import type { contentGateInputs } from "./ContentGate.builder";

export type ContentGateProps = BuilderComponentProps<typeof contentGateInputs> & {
  builderBlock: BuilderBlock;
  builderContext: BuilderContextInterface;
  builderComponents: RegisteredComponents;
};

const isHardGateEnabled = isModelInHardGateConfig("content-gate", env.hardGateConfig);

export const ContentGate: FC<ContentGateProps> = ({
  logoSrc,
  restrictions,
  title,
  whyContentRestricted,
  mapCode,
  restrictionLabel,
  isIPRecogintionEnabled,
  isHardContentGate,
  titleWhileEvaluating,
  builderBlock,
  builderContext,
  builderComponents,
  isDeviceContentGate,
  productFamily,
  deviceLimitationText,
  deviceConfirmText,
  deviceDeclineText,
  deviceBackButtonHref,
}) => {
  const languages = useLanguages();
  const { PLATFORM_CONFIGURATION_URL } = useConfig();
  const commonProps = {
    languages,
    logoSrc,
    title,
    whyContentRestricted,
  };
  const shouldDisplayModelComponent = useDisplayModelComponent("content-gate");
  if (!shouldDisplayModelComponent) {
    return null;
  }

  const externalContent = builderBlock?.children?.map((child) => (
    <Blocks
      key={child.id}
      blocks={[child]}
      parent={builderBlock?.id}
      path="children"
      context={builderContext}
      registeredComponents={builderComponents}
    />
  ));

  if (isEditing()) {
    if (isDeviceContentGate) {
      return (
        <DeviceContentGate
          {...commonProps}
          isAuthorMode={true}
          productFamily={productFamily as ProductFamilyOptionsType}
          limitationText={deviceLimitationText}
          confirmText={deviceConfirmText}
          declineText={deviceDeclineText}
          backButtonHref={deviceBackButtonHref}
        />
      );
    }

    return (
      <ContentGatePopupUI
        {...commonProps}
        externalContent={externalContent}
        isOpen={true}
        restrictions={!isIPRecogintionEnabled ? restrictions : [{ label: restrictionLabel, link: "" }]}
        isAuthorMode={true}
      />
    );
  }

  if (isHardGateEnabled && isHardContentGate) {
    return (
      <HardContentGateUI
        {...commonProps}
        titleWhileEvaluating={titleWhileEvaluating ?? title}
        externalContent={externalContent}
        restrictions={restrictions}
        isOpen={true}
      />
    );
  }

  if (isDeviceContentGate) {
    return (
      <DeviceContentGate
        {...commonProps}
        productFamily={productFamily as ProductFamilyOptionsType}
        limitationText={deviceLimitationText}
        confirmText={deviceConfirmText}
        declineText={deviceDeclineText}
        shouldPageReload={isHardGateEnabled}
        backButtonHref={deviceBackButtonHref}
      />
    );
  }

  if (isIPRecogintionEnabled) {
    const countriesApiUrl = new URL(PLATFORM_CONFIGURATION_URL);
    countriesApiUrl.searchParams.set("codes", mapCode);

    return (
      <ContentGateIP
        {...commonProps}
        externalContent={externalContent}
        countriesApiUrl={countriesApiUrl.toString()}
        restriction={restrictionLabel}
      />
    );
  }
  return <ContentGateUI {...commonProps} externalContent={externalContent} restrictions={restrictions} />;
};
